import React, { useState, useEffect } from 'react';
import { Shield, Lock, Mail, User, Building2, Check, CreditCard, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../config/supabase';
import MercadoPagoCheckoutSimple from '../components/MercadoPagoCheckoutSimple';
import { 
  aplicarMascaraCNPJ, 
  aplicarMascaraTelefone, 
  aplicarMascaraCEP,
  validarEmail, 
  validarCNPJ, 
  validarTelefone, 
  validarSenha,
  validarCEP
} from '../utils/masks';

interface Plano {
  id: string;
  nome: string;
  descricao: string;
  preco: number;
  max_empresas: number;
  max_eleitores: number;
  max_funcionarios: number;
  recursos: string[];
  popular?: boolean;
}

const CadastroPage: React.FC = () => {
  const [step, setStep] = useState<'planos' | 'cadastro' | 'pagamento'>('planos');
  const [planoSelecionado, setPlanoSelecionado] = useState<Plano | null>(null);
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string>('');

  // Debug: verificar se a página está carregando
  React.useEffect(() => {
    console.log('🎯 CadastroPage carregada!');
  }, []);
  
  // Dados do formulário
  const [formData, setFormData] = useState({
    nome_completo: '',
    email: '',
    senha: '',
    confirmar_senha: '',
    telefone: '',
    empresa: '',
    cnpj: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: ''
  });
  
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Carregar planos do banco
  useEffect(() => {
    carregarPlanos();
  }, []);

  const carregarPlanos = async () => {
    try {
      setLoading(true);
      
      // Buscar parâmetros de perfil que representam os planos
      const { data: parametrosData, error } = await supabase
        .from('cp_parametros_perfil')
        .select('*')
        .eq('ativo', true)
        .in('tipo_usuario', ['BASICO', 'PROFISSIONAL', 'ENTERPRISE']);

      if (error) {
        console.error('Erro ao carregar planos:', error);
        return;
      }

      // Mapear para o formato de planos
      const planosMapeados: Plano[] = parametrosData?.map(param => ({
        id: param.id,
        nome: param.tipo_usuario,
        descricao: param.descricao || `Plano ${param.tipo_usuario}`,
        preco: getPrecoPlano(param.tipo_usuario),
        max_empresas: param.max_empresas,
        max_eleitores: param.max_eleitores === -1 ? 999999 : param.max_eleitores,
        max_funcionarios: param.max_funcionarios === -1 ? 999999 : param.max_funcionarios,
        recursos: getRecursosPlano(param.tipo_usuario),
        popular: param.tipo_usuario === 'PROFISSIONAL'
      })) || [];

      // Ordenar os planos na ordem desejada: BASICO, PROFISSIONAL, ENTERPRISE
      const ordemPlanos = ['BASICO', 'PROFISSIONAL', 'ENTERPRISE'];
      planosMapeados.sort((a, b) => {
        const indexA = ordemPlanos.indexOf(a.nome);
        const indexB = ordemPlanos.indexOf(b.nome);
        return indexA - indexB;
      });

      setPlanos(planosMapeados);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPrecoPlano = (tipo: string): number => {
    // Preços padrão - podem ser configurados no admin
    const precos = {
      'BASICO': 29.90,
      'PROFISSIONAL': 79.90,
      'ENTERPRISE': 199.90
    };
    return precos[tipo as keyof typeof precos] || 29.90;
  };

  const getRecursosPlano = (tipo: string): string[] => {
    const recursos = {
      'BASICO': [
        '1 empresa',
        'Até 50 funcionários',
        'Sistema completo CIPA',
        'Relatórios básicos',
        'Suporte por email'
      ],
      'PROFISSIONAL': [
        'Até 3 empresas',
        'Até 200 funcionários',
        'Sistema completo CIPA',
        'Relatórios avançados',
        'Dashboard personalizado',
        'Suporte prioritário',
        'Backup automático'
      ],
      'ENTERPRISE': [
        'Até 10 empresas',
        'Funcionários ilimitados',
        'Sistema completo CIPA',
        'Relatórios personalizados',
        'API de integração',
        'Suporte 24/7',
        'Backup em nuvem',
        'Treinamento personalizado'
      ]
    };
    return recursos[tipo as keyof typeof recursos] || [];
  };

  const handleSelecionarPlano = (plano: Plano) => {
    setPlanoSelecionado(plano);
    setStep('cadastro');
  };

  // Função para validar campos em tempo real
  const validarCampo = (campo: string, valor: string) => {
    const erros = { ...validationErrors };
    
    switch (campo) {
      case 'email':
        if (valor && !validarEmail(valor)) {
          erros.email = 'Email inválido';
        } else {
          delete erros.email;
        }
        break;
      case 'cnpj':
        if (valor && !validarCNPJ(valor)) {
          erros.cnpj = 'CNPJ inválido';
        } else {
          delete erros.cnpj;
        }
        break;
      case 'telefone':
        if (valor && !validarTelefone(valor)) {
          erros.telefone = 'Telefone inválido';
        } else {
          delete erros.telefone;
        }
        break;
      case 'senha':
        const validacaoSenha = validarSenha(valor);
        if (valor && !validacaoSenha.valida) {
          erros.senha = validacaoSenha.erros[0];
        } else {
          delete erros.senha;
        }
        break;
      case 'confirmar_senha':
        if (valor && valor !== formData.senha) {
          erros.confirmar_senha = 'As senhas não coincidem';
        } else {
          delete erros.confirmar_senha;
        }
        break;
      case 'cep':
        if (valor && !validarCEP(valor)) {
          erros.cep = 'CEP inválido';
        } else {
          delete erros.cep;
        }
        break;
    }
    
    setValidationErrors(erros);
  };

  const handleCadastro = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validações finais
    const erros: string[] = [];
    
    if (!validarEmail(formData.email)) {
      erros.push('Email inválido');
    }
    
    if (!validarCNPJ(formData.cnpj)) {
      erros.push('CNPJ inválido');
    }
    
    if (!validarTelefone(formData.telefone)) {
      erros.push('Telefone inválido');
    }
    
    const validacaoSenha = validarSenha(formData.senha);
    if (!validacaoSenha.valida) {
      erros.push(...validacaoSenha.erros);
    }
    
    if (formData.senha !== formData.confirmar_senha) {
      erros.push('As senhas não coincidem');
    }
    
    if (erros.length > 0) {
      setError(erros.join('. '));
      setIsLoading(false);
      return;
    }

    try {
      // Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.senha,
        options: {
          data: {
            nome_completo: formData.nome_completo,
            telefone: formData.telefone
          }
        }
      });

      if (authError) {
        setError(authError.message);
        setIsLoading(false);
        return;
      }

      if (authData.user) {
        // Criar registro na tabela cp_usuarios
        const { error: userError } = await supabase
          .from('cp_usuarios')
          .insert({
            id: authData.user.id,
            email: formData.email,
            nome_completo: formData.nome_completo,
            empresa: formData.empresa || null, // Nome da empresa ou null
            tipo_usuario: planoSelecionado?.nome || 'BASICO',
            ativo: false, // Será ativado após pagamento
            telefone: formData.telefone
          });

        if (userError) {
          setError('Erro ao criar usuário: ' + userError.message);
          setIsLoading(false);
          return;
        }

        // Salvar ID do usuário para usar no pagamento
        setUserId(authData.user.id);

        // Ir para etapa de pagamento
        setStep('pagamento');
      }
    } catch (error) {
      setError('Erro inesperado: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePagamentoSuccess = async (result: any) => {
    try {
      // Ativar usuário após pagamento aprovado
      const { error } = await supabase
        .from('cp_usuarios')
        .update({ ativo: true })
        .eq('id', userId);

      if (error) {
        setError('Erro ao ativar conta: ' + error.message);
        return;
      }

      // Criar empresa se fornecida
      if (formData.empresa && formData.cnpj) {
        const { data: empresaData, error: empresaError } = await supabase
          .from('cp_empresas')
          .insert({
            nome: formData.empresa,
            cnpj: formData.cnpj,
            endereco: formData.endereco,
            telefone: formData.telefone,
            email: formData.email,
            grau_risco: '2', // Padrão
            numero_funcionarios: 0,
            status: 'ATIVA',
            created_by: userId,
            proprietario_id: userId
          })
          .select()
          .single();

        if (empresaError) {
          console.error('Erro ao criar empresa:', empresaError);
        } else if (empresaData) {
          // Criar relacionamento usuário-empresa
          await supabase
            .from('cp_usuario_empresas')
            .insert({
              usuario_id: userId,
              empresa_id: empresaData.id,
              tipo_acesso: 'GESTOR',
              ativo: true
            });
        }
      }

      // Redirecionar para login com mensagem de sucesso
      alert('Conta criada com sucesso! Faça login para acessar o sistema.');
      window.location.href = '/';
    } catch (error) {
      setError('Erro ao finalizar cadastro: ' + (error as Error).message);
    }
  };

  const handlePagamentoError = (error: string) => {
    setError('Erro no pagamento: ' + error);
  };

  const renderPlanos = () => (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Escolha seu Plano
        </h1>
        <p className="text-xl text-gray-600">
          Selecione o plano ideal para sua empresa
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando planos...</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-8">
          {planos.map((plano) => (
            <div
              key={plano.id}
              className={`relative bg-white rounded-2xl shadow-lg p-8 ${
                plano.popular ? 'ring-2 ring-blue-500 scale-105' : ''
              }`}
            >
              {plano.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Mais Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {plano.nome}
                </h3>
                <p className="text-gray-600 mb-4">{plano.descricao}</p>
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  R$ {plano.preco.toFixed(2)}
                </div>
                <p className="text-gray-500 text-sm">por mês</p>
              </div>

              <ul className="space-y-3 mb-8">
                {plano.recursos.map((recurso, index) => (
                  <li key={index} className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{recurso}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelecionarPlano(plano)}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
                  plano.popular
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Escolher Plano
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCadastro = () => (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => setStep('planos')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar aos Planos
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Criar Conta
        </h1>
        <p className="text-gray-600">
          Plano selecionado: <strong>{planoSelecionado?.nome}</strong> - R$ {planoSelecionado?.preco.toFixed(2)}/mês
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleCadastro} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome Completo *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  required
                  value={formData.nome_completo}
                  onChange={(e) => setFormData({...formData, nome_completo: e.target.value})}
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Seu nome completo"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({...formData, email: e.target.value});
                    validarCampo('email', e.target.value);
                  }}
                  className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.email 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="seu@email.com"
                />
              </div>
              {validationErrors.email && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.email}</p>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Senha *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.senha}
                  onChange={(e) => {
                    setFormData({...formData, senha: e.target.value});
                    validarCampo('senha', e.target.value);
                  }}
                  className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.senha 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="Mínimo 8 caracteres com maiúscula, minúscula, número e símbolo"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {validationErrors.senha && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.senha}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Senha *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmar_senha}
                  onChange={(e) => {
                    setFormData({...formData, confirmar_senha: e.target.value});
                    validarCampo('confirmar_senha', e.target.value);
                  }}
                  className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.confirmar_senha 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="Confirme sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {validationErrors.confirmar_senha && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.confirmar_senha}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telefone *
            </label>
            <input
              type="tel"
              required
              value={formData.telefone}
              onChange={(e) => {
                const valorComMascara = aplicarMascaraTelefone(e.target.value);
                setFormData({...formData, telefone: valorComMascara});
                validarCampo('telefone', valorComMascara);
              }}
              className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                validationErrors.telefone 
                  ? 'border-red-300 focus:ring-red-500' 
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="(11) 99999-9999"
            />
            {validationErrors.telefone && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.telefone}</p>
            )}
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Dados da Empresa
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Empresa *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={formData.empresa}
                    onChange={(e) => setFormData({...formData, empresa: e.target.value})}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nome da sua empresa"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CNPJ *
                </label>
                <input
                  type="text"
                  required
                  value={formData.cnpj}
                  onChange={(e) => {
                    const valorComMascara = aplicarMascaraCNPJ(e.target.value);
                    setFormData({...formData, cnpj: valorComMascara});
                    validarCampo('cnpj', valorComMascara);
                  }}
                  className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.cnpj 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="00.000.000/0000-00"
                />
                {validationErrors.cnpj && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.cnpj}</p>
                )}
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CEP
                </label>
                <input
                  type="text"
                  value={formData.cep}
                  onChange={(e) => {
                    const valorComMascara = aplicarMascaraCEP(e.target.value);
                    setFormData({...formData, cep: valorComMascara});
                    validarCampo('cep', valorComMascara);
                  }}
                  className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    validationErrors.cep 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="00000-000"
                />
                {validationErrors.cep && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.cep}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cidade
                </label>
                <input
                  type="text"
                  value={formData.cidade}
                  onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Sua cidade"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  value={formData.estado}
                  onChange={(e) => setFormData({...formData, estado: e.target.value})}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione</option>
                  <option value="AC">Acre</option>
                  <option value="AL">Alagoas</option>
                  <option value="AP">Amapá</option>
                  <option value="AM">Amazonas</option>
                  <option value="BA">Bahia</option>
                  <option value="CE">Ceará</option>
                  <option value="DF">Distrito Federal</option>
                  <option value="ES">Espírito Santo</option>
                  <option value="GO">Goiás</option>
                  <option value="MA">Maranhão</option>
                  <option value="MT">Mato Grosso</option>
                  <option value="MS">Mato Grosso do Sul</option>
                  <option value="MG">Minas Gerais</option>
                  <option value="PA">Pará</option>
                  <option value="PB">Paraíba</option>
                  <option value="PR">Paraná</option>
                  <option value="PE">Pernambuco</option>
                  <option value="PI">Piauí</option>
                  <option value="RJ">Rio de Janeiro</option>
                  <option value="RN">Rio Grande do Norte</option>
                  <option value="RS">Rio Grande do Sul</option>
                  <option value="RO">Rondônia</option>
                  <option value="RR">Roraima</option>
                  <option value="SC">Santa Catarina</option>
                  <option value="SP">São Paulo</option>
                  <option value="SE">Sergipe</option>
                  <option value="TO">Tocantins</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endereço
              </label>
              <input
                type="text"
                value={formData.endereco}
                onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Rua, número, bairro"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
          >
            {isLoading ? 'Criando conta...' : 'Continuar para Pagamento'}
          </button>
        </form>
      </div>
    </div>
  );

  const renderPagamento = () => (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Finalizar Pagamento
        </h1>
        <p className="text-gray-600">
          Complete seu cadastro com o pagamento do plano {planoSelecionado?.nome}
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Resumo do Pedido
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700">Plano {planoSelecionado?.nome}</span>
              <span className="font-semibold">R$ {planoSelecionado?.preco.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Faturamento mensal</span>
              <span>Cancelamento a qualquer momento</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <MercadoPagoCheckoutSimple
          amount={planoSelecionado?.preco || 0}
          description={`Plano ${planoSelecionado?.nome} - CIPA Fácil`}
          userEmail={formData.email}
          userId={userId}
          planId={planoSelecionado?.id || ''}
          planName={planoSelecionado?.nome || ''}
          onSuccess={handlePagamentoSuccess}
          onError={handlePagamentoError}
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {step === 'planos' && renderPlanos()}
      {step === 'cadastro' && renderCadastro()}
      {step === 'pagamento' && renderPagamento()}
    </div>
  );
};

export default CadastroPage;
